(() => {
  // Only target the dedicated Description tab content.
  // (Avoid touching the main product media gallery/thumbnails.)
  const DESCRIPTION_SELECTORS = ['.product-single__description #tab-1 .product-description__content'];

  function toThumbUrl(src) {
    if (!src || !src.includes('cdn.shopify.com')) return src;
    try {
      const url = new URL(src, window.location.href);
      if (!url.searchParams.has('width')) url.searchParams.set('width', '180');
      return url.toString();
    } catch {
      // If URL parsing fails (e.g. malformed), just return original.
      return src;
    }
  }

  function getBestSrc(img) {
    return (
      img.getAttribute('data-src') ||
      img.getAttribute('data-mce-src') ||
      img.currentSrc ||
      img.src ||
      ''
    );
  }

  function normalizeSrcKey(src) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      return url.origin + url.pathname;
    } catch {
      return src.split('?')[0];
    }
  }

  function getProductMediaSources() {
    const mediaImgs = Array.from(
      document.querySelectorAll('.product__media-wrapper .product__media-list .product__media-item img')
    );

    const seen = new Set();
    const sources = [];

    mediaImgs.forEach((img) => {
      const src = getBestSrc(img);
      if (!src) return;
      const key = normalizeSrcKey(src);
      if (seen.has(key)) return;
      seen.add(key);
      sources.push(src);
    });

    return sources;
  }

  function buildModal() {
    const modal = document.createElement('div');
    modal.className = 'desc-lightbox';
    modal.innerHTML = `
      <div class="desc-lightbox__overlay" data-close></div>
      <div class="desc-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Image viewer">
        <div class="desc-lightbox__stage">
          <button class="desc-lightbox__close" type="button" aria-label="Close" data-close>×</button>
          <button class="desc-lightbox__nav desc-lightbox__prev" type="button" aria-label="Previous image" data-prev>‹</button>
          <img class="desc-lightbox__image" alt="" />
          <button class="desc-lightbox__nav desc-lightbox__next" type="button" aria-label="Next image" data-next>›</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const imageEl = modal.querySelector('.desc-lightbox__image');
    const prevBtn = modal.querySelector('[data-prev]');
    const nextBtn = modal.querySelector('[data-next]');

    let images = [];
    let index = 0;

    function syncNav() {
      const canNav = images.length > 1;
      prevBtn.disabled = !canNav;
      nextBtn.disabled = !canNav;
      prevBtn.style.display = canNav ? '' : 'none';
      nextBtn.style.display = canNav ? '' : 'none';
    }

    function showAt(i) {
      if (!images.length) return;
      index = (i + images.length) % images.length;
      const src = images[index];
      imageEl.src = src;
      syncNav();
    }

    function open(newImages, startIndex) {
      images = newImages;
      index = Math.max(0, Math.min(startIndex, images.length - 1));
      showAt(index);
      modal.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
    }

    function close() {
      modal.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      // Avoid flashing previous image next open.
      imageEl.removeAttribute('src');
    }

    modal.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('[data-close]')) {
        close();
        return;
      }
      if (target.closest('[data-prev]')) {
        showAt(index - 1);
        return;
      }
      if (target.closest('[data-next]')) {
        showAt(index + 1);
        return;
      }
      // Click image again to close.
      if (target.classList.contains('desc-lightbox__image')) {
        close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') showAt(index - 1);
      if (e.key === 'ArrowRight') showAt(index + 1);
    });

    return { open };
  }

  function initContainer(container, modal) {
    if (!container.closest('#tab-1')) return;

    // Build a stable thumbnail strip and hide original inline images.
    // Safety: never touch images that are part of the product media gallery.
    if (container.closest('.product__media-wrapper, .product__media-list')) return;
    if (container.closest('.jdgm-widget, .spr-container, .spr-reviews')) return;

    const imgs = Array.from(container.querySelectorAll('img')).filter((img) => {
      const src = getBestSrc(img);
      if (!src) return false;
      if (img.closest('.desc-lightbox')) return false;
      if (img.closest('.jdgm-widget, .spr-container, .spr-reviews')) return false;
      if (
        img.closest(
          '.product__media-wrapper, .product__media-list, .product__media-item, .thumbnail-list, .thumbnail-list-slick'
        )
      ) {
        return false;
      }
      // Ignore extremely small icons.
      const w = img.naturalWidth || img.width || 0;
      const h = img.naturalHeight || img.height || 0;
      if (w && h && w <= 40 && h <= 40) return false;
      return true;
    });

    container.classList.add('desc-gallery');

    // Avoid double-init.
    if (container.querySelector('.desc-thumbs')) return;

    const descriptionSrcs = imgs
      .map((img) => getBestSrc(img))
      .filter(Boolean);

    const mediaSrcs = getProductMediaSources();
    const mergedSrcs = [...mediaSrcs, ...descriptionSrcs];
    const seenSrcs = new Set();
    const fullSrcs = mergedSrcs.filter((src) => {
      const key = normalizeSrcKey(src);
      if (!key || seenSrcs.has(key)) return false;
      seenSrcs.add(key);
      return true;
    });

    if (!fullSrcs.length) return;

    const thumbsLayout = document.createElement('div');
    thumbsLayout.className = 'desc-thumbs-layout';

    const primaryThumbs = document.createElement('div');
    primaryThumbs.className = 'desc-thumbs desc-thumbs--primary';
    primaryThumbs.setAttribute('role', 'list');
    primaryThumbs.setAttribute('aria-label', 'Description images');

    let secondaryThumbs = null;
    if (fullSrcs.length > 4) {
      secondaryThumbs = document.createElement('div');
      secondaryThumbs.className = 'desc-thumbs desc-thumbs--secondary';
      secondaryThumbs.setAttribute('role', 'list');
      secondaryThumbs.setAttribute('aria-label', 'More description images');
    }

    fullSrcs.forEach((src, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'desc-thumb';
      btn.setAttribute('role', 'listitem');
      btn.setAttribute('aria-label', `Open image ${i + 1}`);

      const thumbImg = document.createElement('img');
      thumbImg.src = toThumbUrl(src);
      thumbImg.alt = '';
      thumbImg.loading = 'lazy';

      btn.appendChild(thumbImg);
      btn.addEventListener('click', () => modal.open(fullSrcs, i));
      if (i < 4) {
        primaryThumbs.appendChild(btn);
      } else if (secondaryThumbs) {
        secondaryThumbs.appendChild(btn);
      }
    });

    thumbsLayout.appendChild(primaryThumbs);
    if (secondaryThumbs && secondaryThumbs.children.length) {
      thumbsLayout.appendChild(secondaryThumbs);
    }

    // Insert thumbnails at the top of the description container.
    container.insertBefore(thumbsLayout, container.firstChild);

    // Hide the original images inside the description so varying sizes don't break layout.
    imgs.forEach((img) => {
      img.classList.add('desc-description-gallery__source');
      img.removeAttribute('width');
      img.removeAttribute('height');
      // Remove inline sizing that can force huge banners.
      if (img.style) {
        img.style.width = '';
        img.style.height = '';
        img.style.maxWidth = '';
      }
    });
  }

  function init() {
    const modal = buildModal();

    const containers = DESCRIPTION_SELECTORS.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector))
    );

    containers.forEach((container) => initContainer(container, modal));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
